/**
 * Single source of truth for "what doesn't match between this LS sale
 * and its Xero counterpart". Used by:
 *
 * - the inspector exception table (Issues chip + per-column red
 *   highlighting in `compareExceptions.ts`)
 * - the side-by-side compare panel (`CompareSalesView.tsx`) for its
 *   header summary count
 *
 * Both views must agree on what counts as a mismatch, so neither place
 * computes its own list. This helper centralises the rules.
 *
 * The cross-system status equivalence map (LS ONACCOUNT == Xero
 * AUTHORISED, etc.) is imported from `saleStatusDisplay.ts`, which
 * mirrors `backend/shared/sale_alignment.py::EXPECTED_XERO_FOR_LS_STATUS`.
 * The frontend keeps its own copy because not every code path receives
 * the backend match booleans yet (compare panel reads only the row
 * payload). Update the backend dict and the `saleStatusDisplay.ts`
 * mirror together; this file follows.
 */
import { LS_TO_EXPECTED_XERO } from './saleStatusDisplay'

/**
 * Column / cell key the mismatch belongs to. Inspector table columns
 * use these as their `key`; CompareSalesView uses them only for the
 * tooltip label since its UI maps differently.
 *
 * Object KEYS (date, client, status, amountExTax, amountIncTax, tax,
 * payments, lineItems) are the logical axis names. Object VALUES are
 * the row-column field names the inspector table renders.
 *
 * The backend has a parallel `SALE_MISMATCH_KEYS` tuple in
 * `shared/sale_alignment.py` that lists the chip-strip *filter slugs*
 * (a different surface). The two map as documented on the Python
 * side: Tier 1 axes (date, client, status, lineItems, payments) are
 * 1:1 and locked by `test_status_maps.TestMismatchKeyContract`. Tier 2
 * amounts axes diverge by intent (amountExTax ↔ amounts.exTax etc.) -
 * if you rename a Tier 1 axis here, rename `SALE_MISMATCH_KEYS` in
 * `sale_alignment.py` and `_SHARED_TIER1_AXES` together.
 */
export const SALE_MISMATCH_KEYS = {
  date: 'sale_date',
  client: 'client',
  status: 'status',
  amountExTax: 'ls_amount_ex_tax',
  amountIncTax: 'ls_total_amount',
  tax: 'ls_total_tax',
  payments: 'payments_total',
  lineItems: 'line_items',
} as const

export type SaleMismatchKey = typeof SALE_MISMATCH_KEYS[keyof typeof SALE_MISMATCH_KEYS]

export interface SaleSideValues {
  date?: string | null
  client?: string | null
  status?: string | null
  amount_inc_tax?: number | null
  amount_ex_tax?: number | null
  tax?: number | null
  payments_total?: number | null
}

export interface SaleMismatchInput {
  ls?: SaleSideValues | null
  xero?: SaleSideValues | null
  /**
   * Whether `compare_line_items` reported any line-level errors. The
   * inspector table reads this from `row.sale_line_items_match` (only
   * populated for the base code); the compare panel derives it
   * from `comparison.error_count`. When undefined the helper falls
   * back to comparing the raw line counts.
   */
  lineItemsMismatch?: boolean | null
  /**
   * Raw line counts. Used as a fallback when `lineItemsMismatch` isn't
   * supplied (e.g. discrete codes where the backend doesn't run the
   * line-pass). Different counts -> mismatch.
   */
  lsLineCount?: number | null
  xeroLineCount?: number | null
  /**
   * Backend-computed match booleans. Pass these whenever the API row
   * provides them (any list endpoint that joins sale_alignment's CTE
   * does) so the chip strip respects the canonical SQL match logic
   * instead of re-deriving locally - in particular `clientsMatch`
   * accepts an LS company-name fallback the local `clientsAgree`
   * doesn't model.
   *
   * `null` means "no claim" (missing side); `undefined` means the
   * caller didn't pass the field, in which case we fall back to the
   * local *Agree helpers below.
   */
  statusesMatch?: boolean | null
  amountsExTaxMatch?: boolean | null
  amountsIncTaxMatch?: boolean | null
  taxMatch?: boolean | null
  paymentsMatch?: boolean | null
  clientsMatch?: boolean | null
  datesMatch?: boolean | null
}

export interface SaleMismatch {
  key: SaleMismatchKey
  label: string
}

const roundToCents = (n: number): number => Math.round(Math.abs(n) * 100) / 100

const amountsAgree = (a: number | null | undefined, b: number | null | undefined): boolean => {
  if (a == null || b == null) return true // missing side is "no claim" - don't flag
  return roundToCents(a) === roundToCents(b)
}

// Payments are an additive aggregate: a missing/NULL side semantically
// means "$0 paid" (no payment record), not "unknown". This mirrors the
// backend's `COALESCE(ls.payments_total, 0)` in `sale_alignment.py`. So
// LS=$50 paid + Xero=NULL → mismatch (would otherwise be suppressed by
// the generic amountsAgree).
const paymentsAgree = (a: number | null | undefined, b: number | null | undefined): boolean =>
  roundToCents(a ?? 0) === roundToCents(b ?? 0)

const datesAgree = (a: string | null | undefined, b: string | null | undefined): boolean => {
  const la = (a || '').trim()
  const lb = (b || '').trim()
  if (!la || !lb) return true
  return la === lb
}

const clientsAgree = (a: string | null | undefined, b: string | null | undefined): boolean => {
  const la = (a || '').trim().toLowerCase()
  const lb = (b || '').trim().toLowerCase()
  if (!la || !lb) return true
  return la === lb
}

const statusesAgree = (lsStatus: string | null | undefined, xeroStatus: string | null | undefined): boolean => {
  if (!lsStatus || !xeroStatus) return true
  const expected = LS_TO_EXPECTED_XERO[lsStatus.toUpperCase()]
  if (!expected) return true // unknown LS status - can't claim mismatch
  return expected === xeroStatus.toUpperCase()
}

// Resolve a single mismatch axis: prefer the backend boolean if the
// caller passed it, else fall back to the local *Agree helper. `null`
// from the backend means "no claim" (missing side) which is treated
// as agreeing - same convention the SQL match expressions follow.
const isMismatch = (
  apiMatch: boolean | null | undefined,
  fallback: () => boolean,
): boolean => {
  if (apiMatch === undefined) return !fallback()
  if (apiMatch === null) return false
  return !apiMatch
}

/**
 * Return the list of mismatches for this sale pair. Order is stable
 * so the tooltip and the chip count read the same way across the
 * table and the compare panel.
 *
 * Each axis prefers the backend-computed boolean (`*Match` fields on
 * `input`) when present - that's the canonical sale_alignment verdict.
 * When a caller doesn't have the backend booleans (compare dialog
 * still reads the detail endpoint, which doesn't expose them yet) the
 * local rule-based helpers fire as a fallback.
 */
export function collectSaleMismatches(input: SaleMismatchInput): SaleMismatch[] {
  const issues: SaleMismatch[] = []
  const ls = input.ls
  const xero = input.xero
  if (!ls || !xero) return issues

  if (isMismatch(input.datesMatch, () => datesAgree(ls.date, xero.date))) {
    issues.push({ key: SALE_MISMATCH_KEYS.date, label: 'Date mismatch' })
  }
  if (isMismatch(input.clientsMatch, () => clientsAgree(ls.client, xero.client))) {
    issues.push({ key: SALE_MISMATCH_KEYS.client, label: 'Client mismatch' })
  }
  if (isMismatch(input.statusesMatch, () => statusesAgree(ls.status, xero.status))) {
    issues.push({ key: SALE_MISMATCH_KEYS.status, label: 'Status mismatch' })
  }
  const lineItemsDiffer =
    input.lineItemsMismatch === true ||
    (input.lineItemsMismatch == null
      && input.lsLineCount != null
      && input.xeroLineCount != null
      && input.lsLineCount !== input.xeroLineCount)
  if (lineItemsDiffer) {
    issues.push({ key: SALE_MISMATCH_KEYS.lineItems, label: 'Line items mismatch' })
  }
  if (isMismatch(input.amountsExTaxMatch, () => amountsAgree(ls.amount_ex_tax, xero.amount_ex_tax))) {
    issues.push({ key: SALE_MISMATCH_KEYS.amountExTax, label: 'Amount (ex tax) mismatch' })
  }
  if (isMismatch(input.amountsIncTaxMatch, () => amountsAgree(ls.amount_inc_tax, xero.amount_inc_tax))) {
    issues.push({ key: SALE_MISMATCH_KEYS.amountIncTax, label: 'Amount (inc tax) mismatch' })
  }
  if (isMismatch(input.taxMatch, () => amountsAgree(ls.tax, xero.tax))) {
    issues.push({ key: SALE_MISMATCH_KEYS.tax, label: 'Tax mismatch' })
  }
  if (isMismatch(input.paymentsMatch, () => paymentsAgree(ls.payments_total, xero.payments_total))) {
    issues.push({ key: SALE_MISMATCH_KEYS.payments, label: 'Payments mismatch' })
  }
  return issues
}
